import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Loader2} from 'lucide-react';
import {StateSelect} from '../../components/StateSelect';
import {trpc} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';

const updateGroupSchema = z.object({
  groupName: z.string().trim().min(1, 'Group name is required'),
  address: z.object({
    addressLine1: z.string().trim().min(1, 'Address is required'),
    addressLine2: z.string().trim().optional(),
    city: z.string().trim().min(1, 'City is required'),
    stateCode: z.string().trim().length(2, 'State code must be 2 characters'),
    zipCode: z.string().trim().optional(),
  }),
  primaryContact: z.object({
    firstName: z.string().trim().max(25, 'First name max 25 characters').optional(),
    lastName: z.string().trim().max(25, 'Last name max 25 characters').optional(),
    phoneNumber: z.string().trim().optional(),
    email: z.string().trim().optional(),
  }),
  federalTaxId: z.string().trim().max(20, 'Federal Tax ID max 20 characters').optional(),
  billingCycle: z.string().length(2, 'Billing cycle must be 2 characters'),
});

type UpdateGroupForm = z.infer<typeof updateGroupSchema>;

interface UpdateGroupModalProps {
  open: boolean;
  onClose: () => void;
  groupData: {
    groupId: string;
    name: string | null;
    federalId: string | null;
    originalDate: string | null;
    disenrollDate?: string | null;
    renewalDate?: string | null;
    billingCycle: string | null;
    address?: {
      line1: string;
      line2: string | null;
      city: string | null;
      state: string | null;
      zipCode: string | null;
    } | null;
    primaryContact?: {
      name: string | null;
      email: string | null;
      phone: string | null;
    };
  };
  onSuccess?: () => void;
}

export const UpdateGroupModal = ({open, onClose, groupData, onSuccess}: UpdateGroupModalProps) => {
  const utils = trpc.useUtils();

  // Fetch billing cycles from VBA database
  const {data: billingCyclesData, isLoading: billingCyclesLoading} =
    trpc.vba.groups.groupDetails.getBillingCycles.useQuery(undefined, {
      enabled: open,
    });

  const updateGroupMutation = trpc.vba.groups.groupDetails.update.useMutation({
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Group updated successfully',
      });
      utils.vba.groups.groupDetails.getDetails.invalidate({groupId: groupData.groupId});
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update group',
        variant: 'destructive',
      });
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: {errors},
  } = useForm<UpdateGroupForm>({
    resolver: zodResolver(updateGroupSchema),
    defaultValues: {
      groupName: groupData.name || '',
      address: {
        addressLine1: groupData.address?.line1 || '',
        addressLine2: groupData.address?.line2 || '',
        city: groupData.address?.city || '',
        stateCode: groupData.address?.state || '',
        zipCode: groupData.address?.zipCode || '',
      },
      primaryContact: {
        firstName: '',
        lastName: '',
        phoneNumber: groupData.primaryContact?.phone || '',
        email: groupData.primaryContact?.email || '',
      },
      federalTaxId: groupData.federalId || '',
      billingCycle: groupData.billingCycle || 'MO',
    },
  });

  const billingCycle = watch('billingCycle');

  // Parse the primary contact name into first and last name on mount
  useEffect(() => {
    if (groupData.primaryContact?.name) {
      const nameParts = groupData.primaryContact.name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      setValue('primaryContact.firstName', firstName);
      setValue('primaryContact.lastName', lastName);
    }
  }, [groupData.primaryContact?.name, setValue]);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (open && groupData) {
      reset({
        groupName: groupData.name || '',
        address: {
          addressLine1: groupData.address?.line1 || '',
          addressLine2: groupData.address?.line2 || '',
          city: groupData.address?.city || '',
          stateCode: groupData.address?.state || '',
          zipCode: groupData.address?.zipCode || '',
        },
        primaryContact: {
          firstName: '',
          lastName: '',
          phoneNumber: groupData.primaryContact?.phone || '',
          email: groupData.primaryContact?.email || '',
        },
        federalTaxId: groupData.federalId || '',
        billingCycle: groupData.billingCycle || 'MO',
      });

      if (groupData.primaryContact?.name) {
        const nameParts = groupData.primaryContact.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        setValue('primaryContact.firstName', firstName);
        setValue('primaryContact.lastName', lastName);
      }
    }
  }, [open, groupData, reset, setValue]);

  const onSubmit = (data: UpdateGroupForm) => {
    updateGroupMutation.mutate({
      groupId: groupData.groupId,
      groupName: data.groupName,
      clientName: null,
      address: {
        addressLine1: data.address.addressLine1,
        addressLine2: data.address.addressLine2 || null,
        city: data.address.city,
        stateCode: data.address.stateCode,
        zipCode: data.address.zipCode || null,
      },
      primaryContact: {
        firstName: data.primaryContact.firstName,
        lastName: data.primaryContact.lastName,
        phoneNumber: data.primaryContact.phoneNumber,
        email: data.primaryContact.email,
      },
      originalDate: groupData.originalDate,
      disenrollDate: groupData.disenrollDate || null,
      renewalDate: groupData.renewalDate || '',
      phoneNumber: data.primaryContact.phoneNumber,
      billingCycle: data.billingCycle,
      federalTaxId: data.federalTaxId,
      payorId: '', // This will be handled by the API
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Update Group</DialogTitle>
          <DialogDescription>
            Update the group information. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {/* Basic Information */}
            <div className='space-y-2'>
              <Label htmlFor='groupName'>Group Name *</Label>
              <Input
                id='groupName'
                {...register('groupName')}
                disabled={updateGroupMutation.isPending}
              />
              {errors.groupName && (
                <p className='text-sm text-red-500'>{errors.groupName.message}</p>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='federalTaxId'>Federal Tax ID *</Label>
              <Input
                id='federalTaxId'
                {...register('federalTaxId')}
                disabled={updateGroupMutation.isPending}
              />
              {errors.federalTaxId && (
                <p className='text-sm text-red-500'>{errors.federalTaxId.message}</p>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='billingCycle'>Billing Cycle *</Label>
              <Select
                value={billingCycle}
                onValueChange={(value) => setValue('billingCycle', value)}
                disabled={updateGroupMutation.isPending || billingCyclesLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={billingCyclesLoading ? 'Loading...' : 'Select billing cycle'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {billingCyclesData?.map((cycle) => (
                    <SelectItem key={cycle.id} value={cycle.id}>
                      {cycle.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.billingCycle && (
                <p className='text-sm text-red-500'>{errors.billingCycle.message}</p>
              )}
            </div>
          </div>

          {/* Address Section */}
          <div className='space-y-4'>
            <h3 className='font-semibold text-lg'>Address</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='addressLine1'>Address Line 1 *</Label>
                <Input
                  id='addressLine1'
                  {...register('address.addressLine1')}
                  disabled={updateGroupMutation.isPending}
                />
                {errors.address?.addressLine1 && (
                  <p className='text-sm text-red-500'>{errors.address.addressLine1.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='addressLine2'>Address Line 2</Label>
                <Input
                  id='addressLine2'
                  {...register('address.addressLine2')}
                  disabled={updateGroupMutation.isPending}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='city'>City *</Label>
                <Input
                  id='city'
                  {...register('address.city')}
                  disabled={updateGroupMutation.isPending}
                />
                {errors.address?.city && (
                  <p className='text-sm text-red-500'>{errors.address.city.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='stateCode'>State *</Label>
                <StateSelect
                  value={watch('address.stateCode') || ''}
                  onValueChange={(v) => setValue('address.stateCode', v)}
                  disabled={updateGroupMutation.isPending}
                />
                {errors.address?.stateCode && (
                  <p className='text-sm text-red-500'>{errors.address.stateCode.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='zipCode'>Zip Code</Label>
                <Input
                  id='zipCode'
                  {...register('address.zipCode')}
                  disabled={updateGroupMutation.isPending}
                />
              </div>
            </div>
          </div>

          {/* Primary Contact Section */}
          <div className='space-y-4'>
            <h3 className='font-semibold text-lg'>Primary Contact</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='firstName'>First Name *</Label>
                <Input
                  id='firstName'
                  {...register('primaryContact.firstName')}
                  disabled={updateGroupMutation.isPending}
                />
                {errors.primaryContact?.firstName && (
                  <p className='text-sm text-red-500'>{errors.primaryContact.firstName.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='lastName'>Last Name *</Label>
                <Input
                  id='lastName'
                  {...register('primaryContact.lastName')}
                  disabled={updateGroupMutation.isPending}
                />
                {errors.primaryContact?.lastName && (
                  <p className='text-sm text-red-500'>{errors.primaryContact.lastName.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='email'>Email *</Label>
                <Input
                  id='email'
                  type='email'
                  {...register('primaryContact.email')}
                  disabled={updateGroupMutation.isPending}
                />
                {errors.primaryContact?.email && (
                  <p className='text-sm text-red-500'>{errors.primaryContact.email.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='phoneNumber'>Phone Number *</Label>
                <Input
                  id='phoneNumber'
                  type='tel'
                  {...register('primaryContact.phoneNumber')}
                  disabled={updateGroupMutation.isPending}
                />
                {errors.primaryContact?.phoneNumber && (
                  <p className='text-sm text-red-500'>
                    {errors.primaryContact.phoneNumber.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              disabled={updateGroupMutation.isPending}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={updateGroupMutation.isPending}>
              {updateGroupMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Update Group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
